/**
 * #1014 the work timer. Live work starts only when the person presses **Work it now** (the case header, the ⋯ menu,
 * or a signal's suggestion chip); a chat, call or Riley proposal never starts it. Once started, the global top-bar
 * timer (mounted in the app frame) shows the case, the elapsed time and a notes field that autosaves to the server.
 * The session is the server's `live_work` row, so refresh, navigation and other tabs rehydrate it from
 * `GET /live/me`. **Done for now** reuses the `end_live` card, then offers the time entry prefilled with the minutes
 * and notes. One timer per person: starting on another case asks whether to switch. Owner: #205/#206 (web half).
 */
import { Button, Chip, Input, Textarea } from '@patchbay/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Radio, Timer } from 'lucide-react'
import * as React from 'react'
import { ApiError } from '../board/api'
import { LearnMore } from '../help/learn-more'
import {
  caseLiveQuery,
  type EndOutcome,
  errorText,
  formatElapsed,
  liveApi,
  type MyLiveDto,
  type MyLiveSessionDto,
  myLiveQuery,
  type RemoteWorkCard,
  remoteWorkKeys,
} from './api'
import { outcomeLabel, RemoteWorkCardView } from './cards'

function useCaseLive(caseId: string) {
  return useQuery({ ...caseLiveQuery(caseId), enabled: Boolean(caseId) })
}

/** My open timer (server-owned). */
export function useMyLive() {
  return useQuery(myLiveQuery())
}

/**
 * Work it now: freezes the `go_live` card and confirms it in the same click (the click is the person's explicit
 * choice). A timer on another case asks first; a refusal or a role that cannot confirm leaves the card in place.
 */
export function WorkItNowButton({
  caseId,
  caseNumber,
  trigger = 0,
  hideButton = false,
  size = 'md',
}: {
  caseId: string
  caseNumber?: string
  /** Bumped by the case's ⋯ menu "Work it now". */
  trigger?: number
  /** Only the switch prompt / card / error (the ⋯ menu is the entry point). */
  hideButton?: boolean
  size?: 'sm' | 'md'
}) {
  const qc = useQueryClient()
  const me = useMyLive()
  const [ask, setAsk] = React.useState<MyLiveSessionDto | null>(null)
  const [card, setCard] = React.useState<RemoteWorkCard | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const start = useMutation({
    mutationFn: async (switchTimer: boolean) => {
      const proposed = await liveApi.proposeGoLive(caseId, switchTimer)
      if (!proposed.can_confirm) return { proposed, confirmed: null, failure: null }
      try {
        return { proposed, confirmed: await liveApi.confirm(proposed.id), failure: null }
      } catch (e) {
        return { proposed, confirmed: null, failure: errorText(e) }
      }
    },
    onMutate: () => setError(null),
    onSuccess: ({ proposed, confirmed, failure }) => {
      setAsk(null)
      if (!confirmed || confirmed.state === 'proposed' || confirmed.state === 'failed') {
        setCard(confirmed ?? proposed)
        if (failure) setError(failure)
      }
      void qc.invalidateQueries({ queryKey: remoteWorkKeys.all })
    },
    onError: async (e) => {
      if (e instanceof ApiError && e.code === 'live_elsewhere') {
        const fresh = await qc.fetchQuery({ ...myLiveQuery(), staleTime: 0 }).catch(() => null)
        if (fresh?.session && fresh.session.case_id !== caseId) {
          setAsk(fresh.session)
          return
        }
      }
      setError(errorText(e))
    },
  })
  const open = me.data?.session ?? null
  const begin = React.useCallback(() => {
    if (open && open.case_id !== caseId) setAsk(open)
    else start.mutate(false)
  }, [open, caseId, start])
  const beginRef = React.useRef(begin)
  beginRef.current = begin
  React.useEffect(() => {
    if (trigger > 0) beginRef.current()
  }, [trigger])

  const errorLine = error ? (
    <span role="alert" className="text-xs text-destructive">
      {error}
    </span>
  ) : null
  if (card) {
    return (
      <div className="w-full">
        <RemoteWorkCardView card={card} onDecided={(c) => c.state !== 'proposed' && setCard(null)} />
        {errorLine}
      </div>
    )
  }
  if (ask) {
    return (
      <fieldset
        className="flex w-full flex-wrap items-center gap-2 rounded-md border border-warning/40 bg-warning-soft px-3 py-2 text-sm"
        aria-label="Switch timer"
        data-slot="switch-timer"
      >
        <legend className="sr-only">Switch timer</legend>
        <span className="min-w-0 flex-1 basis-56">
          You're working <strong>{ask.case_number}</strong> now. Switch to {caseNumber ?? 'this case'}? That ends the {ask.case_number}{' '}
          timer and proposes its time entry there.
        </span>
        <Button size="sm" variant="primary" disabled={start.isPending} onClick={() => start.mutate(true)}>
          Switch
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setAsk(null)}>
          Cancel
        </Button>
        {errorLine}
      </fieldset>
    )
  }
  if (hideButton || open?.case_id === caseId) return errorLine
  return (
    <span className="inline-flex items-center gap-2">
      <Button
        size={size}
        variant="primary"
        onClick={begin}
        disabled={start.isPending}
        title="Start your timer on this case: sets the working status and makes you the owner"
        data-slot="work-it-now"
      >
        <Timer /> Work it now
      </Button>
      {errorLine}
    </span>
  )
}

/** A signal (live chat, remote session, check-in) suggests working the case now. It never starts the timer. */
export function LiveSuggestionChip({ caseId, caseNumber }: { caseId: string; caseNumber?: string }) {
  const live = useCaseLive(caseId)
  const suggestion = live.data?.suggestion
  if (!suggestion || live.data?.live.some((r) => r.mine)) return null
  return (
    <span className="inline-flex flex-wrap items-center gap-2" data-slot="live-suggestion">
      <Chip tone="primary" title="Suggested from a signal; nothing has started">
        <Radio aria-hidden /> {suggestion.reason.charAt(0).toUpperCase() + suggestion.reason.slice(1)}
      </Chip>
      <WorkItNowButton caseId={caseId} caseNumber={caseNumber} size="sm" />
    </span>
  )
}

const OUTCOMES: EndOutcome[] = ['waiting', 'resolved', 'keep']
const SAVE_DELAY_MS = 800

type SaveState = 'saved' | 'dirty' | 'saving' | 'error'

/** The global top-bar timer: nothing without an open timer of mine. */
export function WorkTimer() {
  const me = useMyLive()
  // Holds the ended session while Done for now finishes its time entry (the server read is already empty).
  const [held, setHeld] = React.useState<MyLiveSessionDto | null>(null)
  const session = held ?? me.data?.session ?? null
  // A running timer must not disappear because its read failed (hard rule 10); a missing route (404/501) is the
  // feature being off and stays silent.
  const featureOff = me.error instanceof ApiError && (me.error.status === 404 || me.error.status === 501)
  if (!session && me.isError && !featureOff) {
    return (
      <span role="status" className="inline-flex items-center gap-1.5 text-xs text-warning" data-slot="timer-unreachable">
        <Timer aria-hidden className="size-3.5" />
        <span title={errorText(me.error)}>Timer unreachable</span>
        <button type="button" className="cursor-pointer underline" onClick={() => void me.refetch()}>
          Retry
        </button>
      </span>
    )
  }
  if (!session || !me.data) return null
  return (
    <TimerBar
      key={session.id}
      session={session}
      serverNow={me.data.server_now}
      ended={held !== null}
      onEnded={() => setHeld(session)}
      onFinished={() => setHeld(null)}
    />
  )
}

function TimerBar({
  session,
  serverNow,
  ended,
  onEnded,
  onFinished,
}: {
  session: MyLiveSessionDto
  serverNow: string
  ended: boolean
  onEnded: () => void
  onFinished: () => void
}) {
  const qc = useQueryClient()
  // The server's clock, not the client's: elapsed = (now + skew) - started_at.
  const skew = React.useMemo(() => Date.parse(serverNow) - Date.now(), [serverNow])
  const [now, setNow] = React.useState(() => Date.now())
  const [stoppedAt, setStoppedAt] = React.useState<number | null>(null)
  React.useEffect(() => {
    if (ended) return
    const t = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [ended])
  const elapsed = (stoppedAt ?? now) + skew - Date.parse(session.started_at)

  // Notes: local while typing, autosaved (debounced) to the session; the server copy wins when nothing is pending.
  const [notes, setNotes] = React.useState(session.notes ?? '')
  const [save, setSave] = React.useState<SaveState>('saved')
  const pending = React.useRef<string | null>(null)
  const timer = React.useRef<number | null>(null)
  const flush = React.useCallback(async () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
    const value = pending.current
    if (value === null) return
    pending.current = null
    setSave('saving')
    try {
      await liveApi.saveNotes(session.id, value)
      qc.setQueryData<MyLiveDto>(remoteWorkKeys.me, (old) =>
        old?.session?.id === session.id ? { ...old, session: { ...old.session, notes: value } } : old,
      )
      setSave(pending.current === null ? 'saved' : 'dirty')
    } catch {
      if (pending.current === null) pending.current = value
      setSave('error')
    }
  }, [qc, session.id])
  const flushRef = React.useRef(flush)
  flushRef.current = flush
  React.useEffect(() => () => void flushRef.current(), [])
  const serverNotes = session.notes ?? ''
  React.useEffect(() => {
    if (pending.current === null && timer.current === null) setNotes(serverNotes)
  }, [serverNotes])
  const onNotes = (value: string) => {
    setNotes(value)
    pending.current = value
    setSave('dirty')
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => void flush(), SAVE_DELAY_MS)
  }

  return (
    <section
      aria-label="Work timer"
      data-slot="work-timer"
      className="flex flex-wrap items-start gap-x-3 gap-y-2 border-b border-primary/30 bg-primary-soft px-4 py-2"
    >
      <div className="flex shrink-0 items-center gap-2 pt-1">
        <Timer aria-hidden className="size-4 text-primary" />
        <Link to="/case/$id" params={{ id: session.case_number }} className="font-semibold text-primary underline-offset-4 hover:underline">
          {session.case_number}
        </Link>
        <LearnMore topic="work-timer" />
        <span className="font-mono text-sm tabular-nums" data-slot="elapsed" aria-label="Elapsed time" role="timer">
          {formatElapsed(elapsed)}
        </span>
      </div>
      <div className="min-w-0 flex-1 basis-64">
        <label className="sr-only" htmlFor={`timer-notes-${session.id}`}>
          Work notes
        </label>
        <Textarea
          id={`timer-notes-${session.id}`}
          value={notes}
          onChange={(e) => onNotes(e.target.value)}
          onBlur={() => void flush()}
          placeholder="Notes while you work: they become the time entry's note"
          maxLength={4000}
          disabled={ended}
          className="min-h-9 resize-y py-1.5"
          rows={1}
        />
        <p className="mt-0.5 text-2xs text-muted-foreground" aria-live="polite" data-slot="notes-state">
          {save === 'saving'
            ? 'Saving…'
            : save === 'dirty'
              ? 'Typing…'
              : save === 'error'
                ? 'Not saved yet: retrying when you type or leave the field'
                : 'Saved'}
        </p>
      </div>
      <DoneForNowFlow
        session={session}
        beforeEnd={async () => {
          await flush()
          return pending.current === null ? notes : null
        }}
        onEnded={() => {
          setStoppedAt(Date.now())
          onEnded()
        }}
        onFinished={onFinished}
      />
    </section>
  )
}

type Step =
  | { at: 'idle' }
  | { at: 'pick' }
  | { at: 'end'; card: RemoteWorkCard }
  | { at: 'time'; card: RemoteWorkCard }
  | { at: 'no-time'; message: string }

/** Done for now on the timer: the next status (the `end_live` card), then the prefilled time entry. */
function DoneForNowFlow({
  session,
  beforeEnd,
  onEnded,
  onFinished,
}: {
  session: MyLiveSessionDto
  /** Saves pending notes first; null when they could not be saved. */
  beforeEnd: () => Promise<string | null>
  onEnded: () => void
  onFinished: () => void
}) {
  const live = useCaseLive(session.case_id)
  const [step, setStep] = React.useState<Step>({ at: 'idle' })
  const [error, setError] = React.useState<string | null>(null)
  const propose = useMutation({
    mutationFn: async (outcome: EndOutcome) => {
      const saved = await beforeEnd()
      if (saved === null) throw new Error('Your notes are not saved yet; try again in a moment')
      return liveApi.proposeEnd(session.case_id, outcome)
    },
    onMutate: () => setError(null),
    onSuccess: (card) => setStep({ at: 'end', card }),
    onError: (e) => setError(errorText(e)),
  })
  const ended = async (card: RemoteWorkCard) => {
    if (card.state !== 'confirmed' && card.state !== 'edited_confirmed') {
      if (card.state !== 'proposed') setStep({ at: 'idle' })
      return
    }
    onEnded()
    const id = typeof card.result?.time_card_id === 'string' ? card.result.time_card_id : null
    const found = id
      ? (await liveApi.proposals().catch(() => ({ cards: [] as RemoteWorkCard[] }))).cards.find((c) => c.id === id)
      : undefined
    if (found) setStep({ at: 'time', card: found })
    else setStep({ at: 'no-time', message: id ? 'Your time entry is waiting in Proposals.' : 'Timer stopped.' })
  }
  const status: Record<EndOutcome, string | null | undefined> = {
    waiting: live.data?.waiting_status,
    resolved: live.data?.resolved_status,
    keep: live.data?.desk_status,
  }
  const errorLine = error ? (
    <span role="alert" className="text-xs text-destructive">
      {error}
    </span>
  ) : null

  if (step.at === 'idle') {
    return (
      <Button variant="primary" onClick={() => setStep({ at: 'pick' })} className="shrink-0">
        Done for now
      </Button>
    )
  }
  if (step.at === 'pick') {
    return (
      <fieldset className="flex w-full flex-wrap items-center gap-2" aria-label="Next status">
        <legend className="sr-only">Next status</legend>
        <span className="text-xs text-muted-foreground">Next status:</span>
        {OUTCOMES.map((o) => (
          <Button key={o} size="sm" disabled={propose.isPending} onClick={() => propose.mutate(o)}>
            {outcomeLabel(o, status[o])}
          </Button>
        ))}
        <Button size="sm" variant="ghost" onClick={() => setStep({ at: 'idle' })}>
          Keep working
        </Button>
        {errorLine}
      </fieldset>
    )
  }
  if (step.at === 'end') {
    return (
      <div className="w-full">
        <RemoteWorkCardView card={step.card} pickOutcome={false} onDecided={(c) => void ended(c)} />
      </div>
    )
  }
  if (step.at === 'no-time') {
    return (
      <div className="flex w-full items-center gap-2 text-sm">
        <span>{step.message}</span>
        <Button size="sm" variant="ghost" onClick={onFinished}>
          Close
        </Button>
      </div>
    )
  }
  return <TimeEntryStep card={step.card} onFinished={onFinished} />
}

/** The `log_time` card the timer proposed, prefilled with its minutes and notes; edit either before logging. */
function TimeEntryStep({ card, onFinished }: { card: RemoteWorkCard; onFinished: () => void }) {
  const qc = useQueryClient()
  const frozenMinutes = Number(card.payload?.minutes ?? 0)
  const frozenNote = typeof card.payload?.note === 'string' ? card.payload.note : ''
  const [minutes, setMinutes] = React.useState(String(frozenMinutes || ''))
  const [note, setNote] = React.useState(frozenNote)
  const [error, setError] = React.useState<string | null>(null)
  const log = useMutation({
    mutationFn: () => {
      const m = Number.parseInt(minutes, 10)
      const edited: Record<string, unknown> = {}
      if (m !== frozenMinutes) edited.minutes = m
      if (note !== frozenNote) edited.note = note
      return liveApi.confirm(card.id, Object.keys(edited).length ? edited : null)
    },
    onMutate: () => setError(null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: remoteWorkKeys.all })
      onFinished()
    },
    onError: (e) => setError(errorText(e)),
  })
  const valid = /^\d+$/.test(minutes) && Number(minutes) >= 1 && Number(minutes) <= 1440
  return (
    <form
      className="flex w-full flex-wrap items-end gap-2"
      aria-label="Time entry"
      data-slot="timer-time-entry"
      onSubmit={(e) => {
        e.preventDefault()
        if (valid) log.mutate()
      }}
    >
      <label className="flex flex-col text-xs text-muted-foreground">
        Minutes
        <Input inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} className="w-20" aria-label="Minutes" />
      </label>
      <label className="flex min-w-0 flex-1 basis-56 flex-col text-xs text-muted-foreground">
        Note
        <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} aria-label="Note" />
      </label>
      <Button type="submit" variant="primary" disabled={!valid || log.isPending}>
        Log time
      </Button>
      <Button type="button" variant="ghost" onClick={onFinished} title="The time entry stays in Proposals">
        Later
      </Button>
      <p className="w-full text-2xs text-muted-foreground">{card.impact_text}</p>
      {error ? (
        <p role="alert" className="w-full text-xs text-destructive">
          Not logged: {error}
        </p>
      ) : null}
    </form>
  )
}
